import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { message, Form, Input, Descriptions, Tooltip, Tabs } from "antd";
import {
  getSelectedProjectSelector,
  getBlockchainTransactionsSelector,
} from "../Infrastructure/Projects.reducer";
import "antd/dist/antd.min.css";
import "./NewProject.css";
import {
  getProject,
  getProjectBlockchainTransactions,
} from "../Application/Projects.business";
import { CustomFormSelect } from "../../Utils/UI/Text";
import DoubleClickEditLabel from "../../DoubleClickEditLabel/UI/DoubleClickEditLabel";
import { LinkOutlined } from "@ant-design/icons";
import URL from "../../Data/Domain/URL";
import InterfaceTable from "../../Table/InterfaceTable";
import InnerSection from "../../FullProjectDetail/InnerSection";
import { patchProject } from "../Infrastructure/Projects.service";

const { TabPane } = Tabs;

const ProjectInformation = ({ record }) => {
  const { id: routeId } = useParams();
  const project = useSelector(getSelectedProjectSelector);
  const blockchainTransactions = useSelector(getBlockchainTransactionsSelector);

  const [form] = Form.useForm();

  const resolvedId = (record && record.id) || routeId;

  useEffect(() => {
    const fetchProject = async () => {
      try {
        if (resolvedId && !record) {
          await getProject(resolvedId);
        }
      } catch (error) {
        console.error("error:", error);
        message.error("Error al cargar el proyecto");
      }
    };
    fetchProject();
  }, [resolvedId, record]);

  useEffect(() => {
    if (resolvedId) {
      getProjectBlockchainTransactions(resolvedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId]);

  useEffect(() => {
    const effectiveProject = record || project;
    if (effectiveProject) {
      form.setFieldsValue({
        show_without_purchase: effectiveProject.show_without_purchase,
        enabled: effectiveProject.enabled,
        is_available_for_nft: effectiveProject.is_available_for_nft,
        is_available_for_sbt: effectiveProject.is_available_for_sbt,
        is_available_for_egrn: effectiveProject.is_available_for_egrn,
        country_code: effectiveProject.country_code,
        internal_code: effectiveProject.internal_code,
        method: effectiveProject.method,
        url_external_info: effectiveProject.url_external_info,
        fee_egrn: effectiveProject.fee_egrn,
        forwards_equivalent: effectiveProject.forwards_equivalent,
        forwards_minimum: effectiveProject.forwards_minimum,
        forwards_value: effectiveProject.forwards_value,
        forwards_price: effectiveProject.forwards_price,
      });
    }
  }, [project, record, form]);

  const handleFieldUpdate = async (fieldName, newValue) => {
    try {
      const updateData = [
        {
          op: "replace",
          path: `/${fieldName}`,
          value: newValue,
        },
      ];

      const projectId = (record && record.id) || routeId || project?.id;
      await patchProject(projectId, updateData);

      if (projectId) {
        await getProject(projectId);
      }
      const fieldLabels = {
        show_without_purchase: "Disponibilidad de compra",
        is_available_for_nft: "Disponibilidad para NFT",
        is_available_for_sbt: "Disponibilidad para SBT",
        is_available_for_egrn: "Disponibilidad para EGRN",
        url_external_info: "URL de información externa",
      };

      message.success(
        `${fieldLabels[fieldName] || fieldName} actualizado correctamente`
      );
    } catch (error) {
      console.error("patch error:", error?.response || error);
      message.error(`Error al actualizar ${fieldName}`);
    }
  };

  const handleUrlChange = async () => {
    const url = form.getFieldValue("url_external_info");

    if (url !== project?.url_external_info) {
      await handleFieldUpdate("url_external_info", url);
    }
  };

  const publicKey = project?.publicKey;
  const baseUrl = URL.STELLAR_URL;
  const projectUrl = publicKey ? `${baseUrl}accounts/${publicKey}` : null;

  const columns = [
    {
      title: "Hash",
      dataIndex: "hash",
      key: "hash",
      render: (hash) => {
        const txUrl = `${URL.STELLAR_URL}transactions/${hash}`;
        return (
          <Tooltip title="Ver transacción de Stellar">
            <a
              href={txUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hash-transactions"
              style={{ color: "#1677ff" }}
            >
              {hash}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: "Memo",
      dataIndex: "memo",
      key: "memo",
    },
  ];

  return (
    <>
      <Tabs defaultActiveKey="1" centered>
        <TabPane tab="Información General" key="1">
          <InnerSection>
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                show_without_purchase: project?.show_without_purchase,
                is_available_for_nft: project?.is_available_for_nft,
                is_available_for_sbt: project?.is_available_for_sbt,
                is_available_for_egrn: project?.is_available_for_egrn,
                country_code: project?.country_code,
                internal_code: project?.internal_code,
                method: project?.method,
                url_external_info: project?.url_external_info,
                fee_egrn: project?.fee_egrn,
                forwards_equivalent: project?.forwards_equivalent,
                forwards_minimum: project?.forwards_minimum,
                forwards_value: project?.forwards_value,
                forwards_price: project?.forwards_price,
              }}
            >
              <div className="project-info">
                <CustomFormSelect
                  name="show_without_purchase"
                  onChange={(value) =>
                    handleFieldUpdate("show_without_purchase", value)
                  }
                  options={[
                    { value: true, label: "Habilitado para compra" },
                    { value: false, label: "Solo mostrando" },
                  ]}
                  placeholder="Seleccione un tipo"
                  allowClear={false}
                />

                <CustomFormSelect
                  name="is_available_for_nft"
                  onChange={(value) =>
                    handleFieldUpdate("is_available_for_nft", value)
                  }
                  options={[
                    { value: true, label: "Habilitado para NFT" },
                    { value: false, label: "NO Habilitado para NFT" },
                  ]}
                  placeholder="Seleccione un tipo"
                  allowClear={false}
                />

                <CustomFormSelect
                  name="is_available_for_sbt"
                  onChange={(value) =>
                    handleFieldUpdate("is_available_for_sbt", value)
                  }
                  options={[
                    { value: true, label: "Habilitado para SBT" },
                    { value: false, label: "NO Habilitado para SBT" },
                  ]}
                  placeholder="Seleccione un tipo"
                  allowClear={false}
                />

                <CustomFormSelect
                  name="is_available_for_egrn"
                  onChange={(value) =>
                    handleFieldUpdate("is_available_for_egrn", value)
                  }
                  options={[
                    { value: true, label: "Habilitado para EGRN" },
                    { value: false, label: "NO habilitado para EGRN" },
                  ]}
                  placeholder="Seleccione un tipo"
                  allowClear={false}
                />
              </div>
              <div
                style={{
                  padding: 24,
                  borderRadius: 16,
                  background: "rgba(255, 255, 255, 0.65)",
                  backdropFilter: "blur(14px)",
                  boxShadow:
                    "0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
                  marginBottom: 32,
                }}
              >
                <Descriptions
                  layout="vertical"
                  className="custom-descriptions"
                  bordered
                  column={{
                    xxl: 2,
                    xl: 2,
                    lg: 2,
                    md: 1,
                    sm: 1,
                    xs: 1,
                  }}
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Descriptions.Item label="Código de País">
                    <DoubleClickEditLabel
                      str={project?.country_code}
                      labelKey="country_code"
                      type="project"
                      id={resolvedId}
                      onRefresh={() => getProject(resolvedId)}
                      confirmBeforeEdit={true}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Código Interno">
                    <DoubleClickEditLabel
                      str={project?.internal_code}
                      labelKey="internal_code"
                      type="project"
                      id={resolvedId}
                      onRefresh={() => getProject(resolvedId)}
                      confirmBeforeEdit={true}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Método">
                    <DoubleClickEditLabel
                      str={project?.method}
                      labelKey="method"
                      type="project"
                      id={resolvedId}
                      onRefresh={() => getProject(resolvedId)}
                      confirmBeforeEdit={true}
                    />
                  </Descriptions.Item>
                </Descriptions>
                <Descriptions
                  layout="vertical"
                  className="custom-descriptions"
                  bordered
                  column={{
                    xxl: 2,
                    xl: 2,
                    lg: 2,
                    md: 1,
                    sm: 1,
                    xs: 1,
                  }}
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Descriptions.Item label="URL de info externa">
                    <Form.Item name="url_external_info" noStyle>
                      <Input
                        placeholder="Ingrese la URL de información externa"
                        onBlur={handleUrlChange}
                        onPressEnter={handleUrlChange}
                      />
                    </Form.Item>
                  </Descriptions.Item>
                </Descriptions>

                <Descriptions
                  layout="vertical"
                  className="custom-descriptions"
                  bordered
                  column={{
                    xxl: 2,
                    xl: 2,
                    lg: 2,
                    md: 1,
                    sm: 1,
                    xs: 1,
                  }}
                  size="small"
                >
                  <Descriptions.Item label="Fee EGRN">
                    <DoubleClickEditLabel
                      str={project?.fee_egrn}
                      labelKey="fee_egrn"
                      type="project"
                      id={resolvedId}
                      onRefresh={() => getProject(resolvedId)}
                      confirmBeforeEdit={true}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Forwards Equivalent">
                    <DoubleClickEditLabel
                      str={project?.forwards_equivalent}
                      labelKey="forwards_equivalent"
                      type="project"
                      id={resolvedId}
                      onRefresh={() => getProject(resolvedId)}
                      confirmBeforeEdit={true}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Forwards Minimum">
                    <DoubleClickEditLabel
                      str={project?.forwards_minimum}
                      labelKey="forwards_minimum"
                      type="project"
                      id={resolvedId}
                      onRefresh={() => getProject(resolvedId)}
                      confirmBeforeEdit={true}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Forwards Value">
                    <DoubleClickEditLabel
                      str={project?.forwards_value}
                      labelKey="forwards_value"
                      type="project"
                      id={resolvedId}
                      onRefresh={() => getProject(resolvedId)}
                      confirmBeforeEdit={true}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Forwards Price">
                    <DoubleClickEditLabel
                      str={project?.forwards_price}
                      labelKey="forwards_price"
                      type="project"
                      id={resolvedId}
                      onRefresh={() => getProject(resolvedId)}
                      confirmBeforeEdit={true}
                    />
                  </Descriptions.Item>
                </Descriptions>
                <Descriptions
                  layout="vertical"
                  className="custom-descriptions"
                  bordered
                  column={{
                    xxl: 2,
                    xl: 2,
                    lg: 2,
                    md: 1,
                    sm: 1,
                    xs: 1,
                  }}
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Descriptions.Item label="Public Key">
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span>{publicKey || "N/A"}</span>
                      {publicKey && projectUrl && (
                        <Tooltip title="Ver en Stellar">
                          <a
                            href={projectUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <LinkOutlined />
                          </a>
                        </Tooltip>
                      )}
                    </div>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </Form>
          </InnerSection>
        </TabPane>
        <TabPane tab="Transacciones" key="2">
          <InnerSection>
            <InterfaceTable
              rowKey={(record) => record.id}
              data={blockchainTransactions.resources}
              columns={columns}
            />
          </InnerSection>
        </TabPane>
      </Tabs>
    </>
  );
};

export default ProjectInformation;
